import React, { useState, useEffect } from 'react';
import { Save, DollarSign, Loader2, ExternalLink, Clock } from 'lucide-react';
import api from '../services/api';

const PRICING_LABELS = {
  AUTHENTICATION: { label: 'Autenticação', hint: 'Confirmação de identidade (senhas descartáveis, códigos de acesso)' },
  MARKETING: { label: 'Marketing', hint: 'Ofertas, promoções, lembretes de carrinho abandonado' },
  UTILITY: { label: 'Utilidade', hint: 'Confirmações de pedido, avisos de agendamento' },
  SERVICE: { label: 'Serviço', hint: 'Respostas de atendimento dentro da janela de 24h (geralmente grátis)' }
};

function Settings() {
  const [prices, setPrices] = useState({ AUTHENTICATION: '', MARKETING: '', UTILITY: '', SERVICE: '' });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    loadPrices();
  }, []);

  const loadPrices = async () => {
    try {
      const res = await api.get('/api/pricing-config');
      const map = { AUTHENTICATION: '', MARKETING: '', UTILITY: '', SERVICE: '' };
      let latest = null;
      res.data.forEach(p => {
        map[p.category] = p.rate_usd;
        if (!latest || new Date(p.updated_at) > new Date(latest)) latest = p.updated_at;
      });
      setPrices(map);
      setLastUpdated(latest);
    } catch (err) {
      console.error('Erro ao carregar preços:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSavedMsg('');
    try {
      const payload = Object.entries(prices).map(([category, rate_usd]) => ({
        category,
        rate_usd: parseFloat(rate_usd) || 0
      }));
      await api.put('/api/pricing-config', { prices: payload });
      setSavedMsg('Salvo com sucesso!');
      setLastUpdated(new Date().toISOString());
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (err) {
      alert('Erro ao salvar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configurações</h1>
        <p className="text-gray-500">Preferências gerais do sistema.</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-50">
          <div className="flex items-start justify-between gap-4 mb-1">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <DollarSign size={20} className="text-green-600" />
              Preços da Meta por Categoria
            </h3>

            <a
              href="https://whatsappbusiness.com/pt-br/products/platform-pricing/?country=Brasil&currency=D%C3%B3lar%20americano%20(USD)"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap"
            >
              Ver tabela oficial <ExternalLink size={12} />
            </a>
          </div>
          <p className="text-xs text-gray-500">
            Usado para calcular o custo estimado das campanhas de disparo em massa.
          </p>
        </div>

        <div className="p-6 space-y-5">
          {loading ? (
            <div className="py-8 text-center">
              <Loader2 size={24} className="animate-spin mx-auto text-gray-400" />
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(PRICING_LABELS).map(([cat, info]) => (
                  <div key={cat} className="space-y-1.5">
                    <label className="text-sm font-semibold text-gray-700">{info.label}</label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">US$</span>
                      <input
                        type="number"
                        step="0.0001"
                        min="0"
                        value={prices[cat]}
                        onChange={e => setPrices({ ...prices, [cat]: e.target.value })}
                        placeholder="0.0000"
                        className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all"
                      />
                    </div>
                    <p className="text-[11px] text-gray-400">{info.hint}</p>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between pt-5 mt-5 border-t border-gray-50">
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-green-600 rounded-xl text-sm font-bold text-white hover:bg-green-700 disabled:opacity-50 shadow-lg shadow-green-100 transition-all"
                  >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    Salvar Preços
                  </button>
                  {savedMsg && <span className="text-sm text-green-600 font-medium">{savedMsg}</span>}
                </div>
                {lastUpdated && (
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                    <Clock size={12} />
                    Atualizado em {new Date(lastUpdated).toLocaleDateString('pt-BR')}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-blue-50 rounded-xl border border-blue-100 p-4">
        <p className="text-xs text-blue-700 leading-relaxed">
          <strong>Como funciona:</strong> a Meta não expõe o preço exato de cada mensagem por API, só a categoria dela (Marketing, Utilidade, etc). O custo estimado das campanhas é calculado usando os valores configurados aqui, multiplicados pela quantidade de mensagens de cada categoria. Consulte a tabela oficial da Meta periodicamente e atualize os valores aqui quando eles mudarem.
        </p>
      </div>
    </div>
  );
}

export default Settings;
